import React from 'react'

import { newsItemsData } from 'src/data/newsItems'
import { pressReleasesData } from 'src/data/pressReleases'
import Footer from 'src/modules/organisms/footer/Footer'
import Navbar from 'src/modules/organisms/navbar/Navbar'
import FaIcon from 'src/components/partials/FaIcon'
import Layout from 'src/components/partials/Layout'
import SEO from 'src/components/partials/Seo'
import Section, { SectionContainer } from 'src/modules/molecules/section'
import ComponentTitleLockup from 'src/modules/molecules/component-title-lockup'
import ListingCard from 'src/modules/molecules/listing-card'
import './News.css'

// Helper to get asset URL
const getAssetUrl = (asset: any) => {
  const url = asset?.fields?.file?.url ?? asset?.file?.url ?? asset?.url ?? null
  return url?.startsWith('//') ? `https:${url}` : url
}

const ListingsGrid = ({ posts }) => {
  return (
    <div className='grid md:grid-cols-2 gap-2'>
      {posts.map(post => (
        <div key={post.id}>
          <ListingCard
            date={post.publishDate}
            imageSrc={getAssetUrl(post.heroImage)}
            title={post.title}
            url={`/news/${post.urlSlug}/`}
            formatDate
          />
        </div>
      ))}
    </div>
  )
}

const NewsCoverageItem = ({ post }) => {
  const imageUrl = getAssetUrl(post.image)
  return (
    <div className='flex gap-4 items-center border-b-1 border-gray-200 px-2 py-7 justify-between newsCoverageItem'>
      <div className='flex items-center gap-4'>
        <div className='relative w-12 h-12'>
          {imageUrl && (
            <div className='w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden'>
              <img
                src={imageUrl}
                alt=''
                className='rounded-full w-12 h-12 object-cover'
              />
            </div>
          )}
          {!imageUrl && (
            <div className='w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center'>
              <FaIcon
                color='#000'
                size='1.5rem'
                classes='fa-sharp fa-light fa-newspaper'
              />
            </div>
          )}
        </div>

        <h4 className='title-h4'>{post.title}</h4>
      </div>
      <div>
        <p className='text-gray-500 text-xs'>
          {new Date(post.publishDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
      </div>
    </div>
  )
}

const NewsCoverageList = ({ posts }) => {
  return (
    <div className='border-t-1 border-gray-200'>
      {posts.map(post => (
        <a href={`${post.url}`} target='_blank' rel='noreferrer' key={post.id}>
          <NewsCoverageItem post={post} />
        </a>
      ))}
    </div>
  )
}

const NewsPage = () => {
  // Filter out test entries from news items
  const filteredNewsItems = newsItemsData.filter(
    item => !item.internalTitle?.toLowerCase().includes('test')
  )

  return (
    <Layout>
      <Navbar />

      <Section
        paddingSmall='7rem 0 3rem'
        paddingMedium='9rem 0 3rem'
        paddingLarge='12rem 0 3rem'
      >
        <SectionContainer>
          <div className='border-b-1 border-gray-200 pb-8'>
            <ComponentTitleLockup
              headingLevel='h1'
              visualStyle='h2'
              title='In the news'
              bodyText1='Stay up to date with company announcements and press coverage.'
              layout='stacked'
              align='left'
            />
          </div>
        </SectionContainer>
      </Section>

      <Section
        paddingSmall='3rem 0'
        paddingMedium='4rem 0'
        paddingLarge='4rem 0'
      >
        <SectionContainer>
          <ListingsGrid posts={pressReleasesData} />
        </SectionContainer>
      </Section>

      <Section
        paddingSmall='3rem 0 8rem'
        paddingMedium='3rem 0 8rem'
        paddingLarge='3rem 0 8rem'
      >
        <SectionContainer>
          <h2 className='title-h4 mb-6'>News Coverage</h2>
          <NewsCoverageList posts={filteredNewsItems} />
        </SectionContainer>
      </Section>

      <Footer ctaTitle='<h3>Take control<span>of messy data</span></h3>' />
    </Layout>
  )
}

export default NewsPage

export const Head = () => (
  <SEO
    description={`Read the latest Flatfile news, including company announcements, major product launches, funding updates, and press coverage from top media outlets.`}
    title='Flatfile News: Company Announcements & Press Coverage'
  />
)
